import type { FC, ReactNode } from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Picture } from '@ui';
import { barMenuAuthImage } from '@images';
import icons from '@icons';
import AuthFormMain from './stages/AuthFormMain';
import AuthFormPhone from './stages/AuthFormPhone';
import AuthFormEmail from './stages/AuthFormEmail';
import AuthFormRestorePass from './stages/AuthFormRestorePass';
import AuthFormMaxLogin from './stages/AuthFormMaxLogin';
import { observer } from 'mobx-react';
import { cartToken } from '@store/CartStore';
import { useAuthLogin } from './useAuthLogin';
import { saveTinkoffSourceCart } from '@utils/tinkoffSourceCart';
import { ensureTinkoffSdk } from '@utils/tinkoffSdk';
import { getAvailableAuthMethods } from './authProviders';
import API from '@api';
import useWindowSize from '@customHooks/useWindowSize';
// ЭТАП 1 · Шаг 3 — named-импорт: default у либы — объект (двойная
// ESM/CJS-обёртка) → под SSR "Element type is invalid: got object".
import { ReCAPTCHA } from 'react-google-recaptcha';
import type {
  LoginByPhoneAnyWayMutationVariables,
  LoginOrRegisterByEmailMutationVariables,
  LoginOrRegisterBySmsMutationVariables
} from '@schema/cart';
import { PeopleSmsAction } from '@schema/cart';
import LoadingPage from '@components/LoadingPage';
import { autorun } from 'mobx';
import clsx from 'clsx';
import { useHistory } from '@compat/router';
import {
  REFERAL_COURIER_ID_KEY,
  REFERAL_PARTNER_ID_KEY
} from '@root/src/CacheDataRoute';
import { useStores } from '@root/src/stores';
import { v1 as uuidv1 } from 'uuid';
import './index.scss';

const {
  close: { midGrey: closeOutlineMidGrey },
  arrowIosLeft: { midGrey: arrowLeftMidGrey },
  checkmark: { black: checkmarkBlackOutlined }
} = icons.Outline;

const isShowTest = false;
const MAX_LOGIN_STORAGE_KEY = 'max-login';

const getTinkoffConfig = (): {
  redirectUri: string;
  responseType: string;
  clientId: string;
  state: string;
} => ({
  redirectUri: 'https://www.delikateska.ru/auth/tinkoff',
  responseType: 'code',
  clientId: 'tid_delikateska',
  state: uuidv1()
});

const getTinkoffWlConfig = (): {
  redirectUri: string;
  responseType: string;
  scopeParameters: string;
  clientId: string;
  state: string;
} => ({
  redirectUri: 'https://www.delikateska.ru/auth/tinkoff',
  responseType: 'code',
  clientId: 'tid_delikateska',
  state: uuidv1(),
  scopeParameters: ''
});

export const TINKOFF_INPUT_CLASSNAME = 'tinkoff-input';
export const TINKOFF_BTN_CLASSNAME = 'tinkoff-wl-button';
export const TINKOFF_WL_HOST_CLASSNAME = 'tinkoff-wl-host';
export const TINKOFF_CONTAINER_CLASSNAME = 'tbank-button-container';
export const TINKOFF_ICON_CLASSNAME = 'tbank-button-icon';

const tinkoffBtnConfig = {
  target: '_self' as const,
  input: `.${TINKOFF_INPUT_CLASSNAME}`,
  actionElement: `.${TINKOFF_BTN_CLASSNAME}`,
  percentageAuth: 100
};

const tinkoffButtonUiConfig = {
  size: 'm' as const,
  color: 'primary' as const,
  text: 'Т-Банк',
  textPosition: 'left' as const,
  target: '_self' as const,
  recognized: true
};

interface AuthFormProps {
  isBarMenu?: boolean;
  initialPhone?: string;
  isBarMenuSuccess?: boolean;
  courierId?: string | null;
  partnerId?: string | null;
  isRegisterPage?: boolean;
  closeModal?: () => void;
  onLogin?: () => void;
}

interface Recaptcha {
  reset: () => void;
  executeAsync: () => Promise<void>;
}

export type FormStage =
  | 'loading'
  | 'already-logged'
  | 'main'
  | 'referal-register'
  | 'phone-login'
  | 'call-login'
  | 'tg-login'
  | 'max-login'
  | 'email-login'
  | 'restore-pass';

const AuthForm: FC<AuthFormProps> = observer(
  ({
    isBarMenu,
    isBarMenuSuccess,
    initialPhone,
    courierId,
    partnerId,
    isRegisterPage,
    closeModal,
    onLogin
  }) => {
    const { userStore, modalsStore, configStore, cartStore, barMenuStore } =
      useStores();
    const history = useHistory();
    const { loginErrorMsg } = modalsStore;
    const [userExists, setUserExists] = useState(true);
    const [onLoginOrRegisterError, setOnLoginOrRegisterError] = useState('');
    const [maxLoginData, setMaxLoginData] = useState<{
      code: string;
      link: string;
      ttl: number;
    } | null>(null);
    const [maxLoginError, setMaxLoginError] = useState('');
    const [maxLoginStatus, setMaxLoginStatus] = useState('');
    const shouldOpenMaxRef = useRef(false);

    const [barMenuOtpAction, setBarMenuOtpAction] = useState<PeopleSmsAction>(
      PeopleSmsAction.Flashcall
    );

    const recaptchaRef = useRef<Recaptcha | null>(null);

    const isMenuAuth =
      isBarMenu || history.location.pathname.startsWith('/bar-menu');

    const { isMobile, viewportHeight } = useWindowSize();
    const [formStage, setFormStage] = useState<FormStage>('loading');
    const [isShowCAPTCHA, setIsShowCAPTCHA] = useState(true);
    const [userCredentials, setUserCredentials] = useState({
      email: '',
      phone: ''
    });

    const mountTinkoffButtonRef = useRef<() => boolean>(() => false);
    const tinkoffMountInProgressRef = useRef(false);
    const tinkoffClickCleanupsRef = useRef<Map<HTMLElement, () => void>>(
      new Map()
    );
    const saveTinkoffCartRef = useRef<() => void>(() => undefined);
    const wlBoundInputRef = useRef<Element | null>(null);
    const wlBoundButtonRef = useRef<Element | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    saveTinkoffCartRef.current = (): void => {
      saveTinkoffSourceCart(cartStore?.cart?.cookieId || cartToken);
    };

    const mountTinkoffButton = useCallback((): boolean => {
      if (isMenuAuth) {
        return false;
      }
      const { data } = configStore;

      if (typeof TidSDK === 'undefined' || data?.tinkoff_auth_partner !== '1') {
        return false;
      }

      if (tinkoffMountInProgressRef.current) {
        return false;
      }

      const containers = [TINKOFF_CONTAINER_CLASSNAME, TINKOFF_ICON_CLASSNAME]
        .map(className => ({
          className,
          node: document.querySelector(`.${className}`)
        }))
        .filter(
          (item): item is { className: string; node: HTMLElement } =>
            item.node instanceof HTMLElement &&
            item.node.childElementCount === 0
        );

      if (containers.length === 0) {
        return false;
      }

      tinkoffMountInProgressRef.current = true;

      try {
        const cleanups = tinkoffClickCleanupsRef.current;
        cleanups.forEach((cleanup, boundNode) => {
          if (!boundNode.isConnected) {
            cleanup();
            cleanups.delete(boundNode);
          }
        });

        containers.forEach(({ className, node }) => {
          if (!cleanups.has(node)) {
            const onClick = (): void => {
              saveTinkoffCartRef.current();
            };
            node.addEventListener('click', onClick, true);
            cleanups.set(node, () => {
              node.removeEventListener('click', onClick, true);
            });
          }
          const tidSdk = new TidSDK(getTinkoffConfig());
          tidSdk.addButton({
            ...tinkoffButtonUiConfig,
            container: `.${className}`
          });
        });
        return true;
      } finally {
        tinkoffMountInProgressRef.current = false;
      }
    }, [configStore, isMenuAuth]);

    const bindTinkoffWhiteLabel = useCallback(
      (force = false): void => {
        if (isMenuAuth) {
          return;
        }
        if (
          typeof TidSDK === 'undefined' ||
          configStore.data?.tinkoff_auth_partner !== '1'
        ) {
          return;
        }

        const input = document.querySelector(`.${TINKOFF_INPUT_CLASSNAME}`);
        const host = document.querySelector(`.${TINKOFF_WL_HOST_CLASSNAME}`);
        if (
          !(input instanceof HTMLInputElement) ||
          !(host instanceof HTMLElement)
        ) {
          return;
        }

        let button = host.querySelector(`button.${TINKOFF_BTN_CLASSNAME}`);
        const alreadyBound =
          button instanceof HTMLElement &&
          wlBoundInputRef.current === input &&
          wlBoundButtonRef.current === button;
        if (!force && alreadyBound) {
          return;
        }

        if (button instanceof HTMLElement) {
          button.remove();
        }
        const fresh = document.createElement('button');
        fresh.type = 'button';
        fresh.className = TINKOFF_BTN_CLASSNAME;
        fresh.tabIndex = -1;
        fresh.setAttribute('aria-hidden', 'true');
        host.appendChild(fresh);
        button = fresh;

        wlBoundInputRef.current = input;
        wlBoundButtonRef.current = button;
        const tidSdk = new TidSDK(getTinkoffWlConfig());
        tidSdk.addWL(tinkoffBtnConfig);
      },
      [configStore, isMenuAuth]
    );

    mountTinkoffButtonRef.current = mountTinkoffButton;

    const reinitializeTinkoff = useCallback((): void => {
      mountTinkoffButtonRef.current();
      bindTinkoffWhiteLabel();
    }, [bindTinkoffWhiteLabel]);

    const scheduleTinkoffButtonClick = useCallback((): void => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        timer.current = null;
        bindTinkoffWhiteLabel(true);
        const btn = document.querySelector(`button.${TINKOFF_BTN_CLASSNAME}`);
        if (btn instanceof HTMLElement) {
          btn.click();
        }
      }, 100);
    }, [bindTinkoffWhiteLabel]);

    useEffect(() => {
      if (partnerId) {
        localStorage.setItem(REFERAL_PARTNER_ID_KEY, partnerId);
      }
      if (courierId) {
        localStorage.setItem(REFERAL_COURIER_ID_KEY, courierId);
      }
    }, [courierId, partnerId]);

    useEffect(() => {
      if (formStage !== 'loading' && formStage !== 'already-logged') {
        bindTinkoffWhiteLabel();
      }
    }, [formStage, bindTinkoffWhiteLabel]);

    // SDK Тинькофф больше не грузится на каждой странице (src/utils/tinkoffSdk.ts):
    // если к открытию формы его ещё нет — догружаем и монтируем кнопки
    useEffect(() => {
      if (isMenuAuth || typeof TidSDK !== 'undefined') {
        return;
      }
      let cancelled = false;
      void ensureTinkoffSdk().then(() => {
        if (!cancelled) {
          reinitializeTinkoff();
        }
      });
      return () => {
        cancelled = true;
      };
    }, [isMenuAuth, reinitializeTinkoff]);

    useEffect(
      () => () => {
        if (timer.current !== null) {
          clearTimeout(timer.current);
        }
        const cleanups = tinkoffClickCleanupsRef.current;
        cleanups.forEach(cleanup => {
          cleanup();
        });
        cleanups.clear();
      },
      []
    );

    useEffect(() => {
      setOnLoginOrRegisterError('');
    }, [formStage]);

    useEffect(() => {
      const disposer = autorun(() => {
        const { isUserLoading, isUserLoggedIn } = userStore;
        if (!isUserLoading) {
          if (isUserLoggedIn) {
            setFormStage('already-logged');
          } else {
            setFormStage('main');
          }
        }
      });
      return () => {
        if (disposer) {
          disposer();
        }
      };
    }, [userStore]);

    const goToMainStage = useCallback((): void => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      wlBoundInputRef.current = null;
      wlBoundButtonRef.current = null;
      setFormStage('main');
    }, []);

    const getUserCredentials = useCallback(
      ({ field, value }: { field: string; value: string }): void => {
        setUserCredentials(prev => ({ ...prev, [field]: value }));
      },
      []
    );

    const login = useAuthLogin({ isBarMenu, onLogin });

    const startMaxLogin = useCallback(async (): Promise<void> => {
      setMaxLoginError('');
      setMaxLoginStatus('');
      try {
        const response = await API.maxLoginStart();
        const { success, error, code, link, ttl } = response.maxLoginStart;
        if (!success || !code || !link || typeof ttl !== 'number') {
          setOnLoginOrRegisterError(
            error || 'Вход через MAX сейчас недоступен'
          );
          return;
        }
        sessionStorage.setItem(
          MAX_LOGIN_STORAGE_KEY,
          JSON.stringify({
            code,
            link,
            expiresAt: Date.now() + ttl * 1000
          })
        );
        shouldOpenMaxRef.current = true;
        setMaxLoginData({ code, link, ttl });
        setFormStage('max-login');
      } catch (error) {
        setOnLoginOrRegisterError(
          error[0]?.message || 'Вход через MAX сейчас недоступен'
        );
      }
    }, []);

    const checkMaxLogin = useCallback(
      async (code: string, options?: { manual?: boolean }): Promise<void> => {
        try {
          const response = await API.maxLoginCheck({ code });
          const { success, error, errorCode, people } = response.maxLoginCheck;
          if (success && people?.cookie_id) {
            const { cookie_id: cookieId, cartCookieId, settingsItem } = people;
            sessionStorage.removeItem(MAX_LOGIN_STORAGE_KEY);
            setMaxLoginStatus('');
            setMaxLoginData(null);
            login({ cartCookieId, cookieId, settingsItem });
            return;
          }
          if (errorCode === 'WAIT' || (!success && !errorCode && !error)) {
            if (options?.manual) {
              setMaxLoginStatus(
                'Ещё не подтверждено в MAX. Подождите пару секунд и нажмите снова.'
              );
            }
            return;
          }
          sessionStorage.removeItem(MAX_LOGIN_STORAGE_KEY);
          setMaxLoginStatus('');
          setMaxLoginError(
            error ||
              (errorCode === 'EXPIRED'
                ? 'Время ожидания истекло'
                : 'Не удалось подтвердить вход')
          );
        } catch (error) {
          if (options?.manual) {
            setMaxLoginStatus(
              error?.[0]?.message || 'Не удалось проверить. Попробуйте ещё раз.'
            );
          }
        }
      },
      [login]
    );

    useEffect(() => {
      if (!isMenuAuth || formStage !== 'main') {
        return;
      }

      const savedData = sessionStorage.getItem(MAX_LOGIN_STORAGE_KEY);
      if (!savedData) {
        return;
      }

      try {
        const { code, link, expiresAt } = JSON.parse(savedData) as {
          code?: unknown;
          link?: unknown;
          expiresAt?: unknown;
        };
        if (
          typeof code !== 'string' ||
          typeof link !== 'string' ||
          typeof expiresAt !== 'number'
        ) {
          sessionStorage.removeItem(MAX_LOGIN_STORAGE_KEY);
          return;
        }
        const secondsLeft = Math.ceil((expiresAt - Date.now()) / 1000);
        if (!code || !link || secondsLeft <= 0) {
          sessionStorage.removeItem(MAX_LOGIN_STORAGE_KEY);
          return;
        }
        setMaxLoginData({ code, link, ttl: secondsLeft });
        setFormStage('max-login');
      } catch {
        sessionStorage.removeItem(MAX_LOGIN_STORAGE_KEY);
      }
    }, [formStage, isMenuAuth]);

    useEffect(() => {
      if (formStage !== 'max-login' || !maxLoginData || maxLoginError) {
        return;
      }

      const check = (): void => {
        void checkMaxLogin(maxLoginData.code);
      };
      check();
      const interval = setInterval(check, 2000);
      const onVisible = (): void => {
        if (!document.hidden) {
          check();
        }
      };
      document.addEventListener('visibilitychange', onVisible);

      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisible);
      };
    }, [checkMaxLogin, formStage, maxLoginData, maxLoginError]);

    useEffect(() => {
      if (
        formStage === 'max-login' &&
        maxLoginData &&
        isMobile &&
        shouldOpenMaxRef.current
      ) {
        shouldOpenMaxRef.current = false;
        window.location.assign(maxLoginData.link);
      }
    }, [formStage, isMobile, maxLoginData]);

    const exitMaxLogin = useCallback((): void => {
      sessionStorage.removeItem(MAX_LOGIN_STORAGE_KEY);
      setMaxLoginData(null);
      setMaxLoginError('');
      setMaxLoginStatus('');
      setFormStage('main');
    }, []);

    const authViaSms = useCallback(
      async ({
        phone,
        code = null
      }: {
        phone: string;
        code?: number | null;
      }): Promise<void> => {
        if (
          !isMenuAuth &&
          !code &&
          configStore.data?.tinkoff_auth_partner === '1'
        ) {
          saveTinkoffSourceCart(cartStore?.cart?.cookieId || cartToken);
          scheduleTinkoffButtonClick();
          return;
        }

        const token = await recaptchaRef?.current?.executeAsync();
        try {
          const payload: LoginOrRegisterBySmsMutationVariables = {
            phone,
            code,
            action: PeopleSmsAction.Login,
            partnerId: partnerId ? +partnerId : undefined,
            token: '',
            isMenu: isMenuAuth
          };

          if (token) {
            payload.token = token;
          }

          if (courierId && !userExists) {
            payload.courierId = +courierId;
          }

          const response = await API.loginOrRegisterBySms(payload);
          if (
            !response.loginOrRegisterBySms.success &&
            response.loginOrRegisterBySms.errorCode === '403'
          ) {
            recaptchaRef.current?.reset();
          }
          if (response.loginOrRegisterBySms.success && !code) {
            setFormStage('phone-login');
          }
          if (response.loginOrRegisterBySms?.people) {
            const {
              cookie_id: cookieId,
              cartCookieId,
              settingsItem
              // id
            } = response.loginOrRegisterBySms.people;
            if (cookieId) {
              login({ cartCookieId, cookieId, settingsItem });
            }

            localStorage.removeItem('courierId');
          }
        } catch (error) {
          setOnLoginOrRegisterError(error[0].message);
        }
      },
      [
        cartStore,
        configStore,
        courierId,
        isMenuAuth,
        login,
        partnerId,
        scheduleTinkoffButtonClick,
        userExists
      ]
    );

    const authViaCall = useCallback(
      async ({
        phone,
        code = null
      }: {
        phone: string;
        code?: number | null;
      }): Promise<void> => {
        const token = await recaptchaRef?.current?.executeAsync();

        try {
          const payload: LoginOrRegisterBySmsMutationVariables = {
            phone,
            code,
            action: 'FLASHCALL' as any,
            partnerId: partnerId ? +partnerId : undefined,
            token: '',
            isMenu: isMenuAuth
          };

          if (token) {
            payload.token = token;
          }

          if (courierId && !userExists) {
            payload.courierId = +courierId;
          }

          const response = await API.loginOrRegisterBySms(payload);
          if (
            !response.loginOrRegisterBySms.success &&
            response.loginOrRegisterBySms.errorCode === '403'
          ) {
            recaptchaRef.current?.reset();
          }
          if (response.loginOrRegisterBySms.success && !code) {
            setFormStage('call-login');
          }
          if (response.loginOrRegisterBySms?.people) {
            const {
              cookie_id: cookieId,
              cartCookieId,
              settingsItem
              // id
            } = response.loginOrRegisterBySms.people;
            if (cookieId) {
              login({ cartCookieId, cookieId, settingsItem });
            }

            localStorage.removeItem('courierId');
          }
        } catch (error) {
          setOnLoginOrRegisterError(error[0].message);
        }
      },
      [courierId, login, partnerId, userExists, isMenuAuth]
    );

    const authViaTg = useCallback(
      async ({
        phone,
        code = null
      }: {
        phone: string;
        code?: number | null;
      }): Promise<void> => {
        const token = await recaptchaRef?.current?.executeAsync();

        try {
          const payload: LoginOrRegisterBySmsMutationVariables = {
            phone,
            code,
            action: 'TGAUTH' as any,
            partnerId: partnerId ? +partnerId : undefined,
            token: '',
            isMenu: isMenuAuth
          };

          if (token) {
            payload.token = token;
          }

          if (courierId && !userExists) {
            payload.courierId = +courierId;
          }

          const response = await API.loginOrRegisterBySms(payload);
          if (
            !response.loginOrRegisterBySms.success &&
            response.loginOrRegisterBySms.errorCode === '403'
          ) {
            recaptchaRef.current?.reset();
          }
          if (response.loginOrRegisterBySms.success && !code) {
            setFormStage('tg-login');
          }
          if (response.loginOrRegisterBySms?.people) {
            const {
              cookie_id: cookieId,
              cartCookieId,
              settingsItem
            } = response.loginOrRegisterBySms.people;
            if (cookieId) {
              login({ cartCookieId, cookieId, settingsItem });
            }

            localStorage.removeItem('courierId');
          }
        } catch (error) {
          setOnLoginOrRegisterError(error[0].message);
        }
      },
      [courierId, login, partnerId, userExists, isMenuAuth]
    );

    const authViaLoginByPhoneAnyWay = useCallback(
      async ({
        phone
      }: LoginByPhoneAnyWayMutationVariables): Promise<{
        peopleExist: boolean;
        isLoggedIn: boolean;
      } | null> => {
        try {
          const storeId = barMenuStore?.currentStore?.id;
          const response = await API.loginByPhoneAnyWay({ phone, storeId });
          const people = response.loginByPhoneAnyWay?.people;
          const isLoggedIn = !!(
            response.loginByPhoneAnyWay?.success && people?.cookie_id
          );
          if (isLoggedIn && people) {
            const { cookie_id: cookieId, cartCookieId, settingsItem } = people;
            if (cookieId) {
              login({ cartCookieId, cookieId, settingsItem });
            }
            localStorage.removeItem('courierId');
          }
          return {
            peopleExist: response.loginByPhoneAnyWay?.peopleExist || false,
            isLoggedIn
          };
        } catch (error) {
          setOnLoginOrRegisterError(error[0].message);
          return null;
        }
      },
      [login, barMenuStore]
    );

    const barMenuAuthBySms = useCallback(
      async ({
        phone,
        code = null,
        action
      }: {
        phone: string;
        code?: number | null;
        action: PeopleSmsAction;
      }): Promise<void> => {
        const token = await recaptchaRef?.current?.executeAsync();
        try {
          const payload: LoginOrRegisterBySmsMutationVariables = {
            phone,
            code,
            action,
            token: token || '',
            isMenu: true
          };

          const response = await API.loginOrRegisterBySms(payload);
          if (
            !response.loginOrRegisterBySms.success &&
            response.loginOrRegisterBySms.errorCode === '403'
          ) {
            recaptchaRef.current?.reset();
          }

          const forwardToCodeEntry =
            !code &&
            (response.loginOrRegisterBySms.success ||
              (response.loginOrRegisterBySms.errorCode === 'repeatTimeout' &&
                formStage !== 'phone-login'));
          if (forwardToCodeEntry) {
            setBarMenuOtpAction(action);
            setOnLoginOrRegisterError('');
            setFormStage('phone-login');
          }
          if (!response.loginOrRegisterBySms.success && !forwardToCodeEntry) {
            setOnLoginOrRegisterError(
              response.loginOrRegisterBySms.error ||
                response.loginOrRegisterBySms.info ||
                (code ? 'Неверный код' : 'Не удалось отправить код')
            );
          }
          if (response.loginOrRegisterBySms?.people) {
            const {
              cookie_id: cookieId,
              cartCookieId,
              settingsItem
            } = response.loginOrRegisterBySms.people;
            if (cookieId) {
              login({ cartCookieId, cookieId, settingsItem });
            }

            localStorage.removeItem('courierId');
          }
        } catch (error) {
          setOnLoginOrRegisterError(error[0].message);
        }
      },
      [login, formStage]
    );

    const barMenuRequestCall = useCallback(
      async ({ phone }: { phone: string }): Promise<void> => {
        await barMenuAuthBySms({
          phone,
          code: null,
          action: PeopleSmsAction.Flashcall
        });
      },
      [barMenuAuthBySms]
    );

    const barMenuRequestSms = useCallback(
      async ({ phone }: { phone: string }): Promise<void> => {
        await barMenuAuthBySms({
          phone,
          code: null,
          action: PeopleSmsAction.Login
        });
      },
      [barMenuAuthBySms]
    );

    const barMenuOtpVerify = useCallback(
      async ({
        phone,
        code
      }: {
        phone: string;
        code?: number | null;
      }): Promise<void> => {
        if (code === -1024) {
          setOnLoginOrRegisterError('');
          await barMenuAuthBySms({
            phone,
            code: null,
            action: barMenuOtpAction
          });
          return;
        }
        await barMenuAuthBySms({ phone, code, action: barMenuOtpAction });
      },
      [barMenuAuthBySms, barMenuOtpAction]
    );

    const authViaEmail = async ({ email, password }): Promise<void> => {
      const data: LoginOrRegisterByEmailMutationVariables['data'] = {
        email,
        password,
        partnerId: partnerId ? +partnerId : undefined
      };

      if (courierId && !userExists) {
        data.courierId = +courierId;
      }

      const dataAction = userExists
        ? PeopleSmsAction.Login
        : PeopleSmsAction.Register;
      await API.loginOrRegisterByEmail({ data, action: dataAction })
        .then(res => {
          if (res.loginOrRegisterByEmail?.people) {
            const {
              cookie_id: cookieId,
              cartCookieId,
              settingsItem
            } = res.loginOrRegisterByEmail.people;
            if (cookieId) {
              login({ cartCookieId, cookieId, settingsItem });
            }
            localStorage.removeItem('courierId');
          }
        })
        .catch(err => {
          console.log('Error: ', err);
        });
    };

    const resetPass = (email: string): void => {
      API.resetPass({ email }).catch(err => {
        console.log('Error ', err);
      });
    };
    const goToRestorePassModal = (): void => {
      setFormStage('restore-pass');
    };

    const redoCaptcha = useCallback(() => {
      recaptchaRef.current?.reset();
      recaptchaRef?.current?.executeAsync();
    }, []);

    const isBarMenuSuccessOtpStage =
      !!isBarMenuSuccess &&
      (formStage === 'phone-login' || formStage === 'tg-login');

    const renderBarMenuSuccessOtp = (node: ReactNode): ReactNode =>
      isBarMenuSuccess ? (
        <Modal
          open
          framed
          placementMobile='bottom-sheet'
          onClose={goToMainStage}
          onBack={goToMainStage}
          overlayStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          className='w-[387px] max-w-[387px] sm:w-full sm:max-w-full'
        >
          {node}
        </Modal>
      ) : (
        node
      );

    return (
      <div
        style={
          isMobile && !isBarMenu
            ? { height: isRegisterPage ? 'auto' : `${viewportHeight}px` }
            : {}
        }
        className={clsx(
          `AuthForm`,
          formStage === 'referal-register' && ' AuthForm--referal',
          isRegisterPage && 'AuthForm--register',
          isBarMenu && 'AuthForm--bar-menu',
          isBarMenuSuccess && 'AuthForm--bar-menu-success'
        )}
      >
        <div className='AuthForm__header'>
          {![
            'loading',
            'already-logged',
            'main',
            'referal-register',
            'phone-login',
            'call-login',
            'tg-login',
            'max-login'
          ].includes(formStage) && (
            <Picture
              icon={arrowLeftMidGrey}
              onClick={goToMainStage}
              height='24'
              width='24'
              className='AuthForm__arrow-back'
            />
          )}
          {closeModal && (
            <Picture
              pictureClassName='AuthForm__cross'
              icon={closeOutlineMidGrey}
              onClick={closeModal}
              height='24'
              width='24'
            />
          )}
        </div>
        {configStore?.data?.miadzaki_forever && isShowCAPTCHA && (
          <ReCAPTCHA
            ref={recaptchaRef}
            size='invisible'
            badge='bottomright'
            sitekey={configStore?.data?.miadzaki_forever}
            onExpired={redoCaptcha}
          />
        )}

        {formStage === 'loading' && (
          <LoadingPage fullHeight className='m-auto' />
        )}
        {formStage === 'already-logged' && (
          <div className='flex flex-col items-center justify-center w-full h-full m-auto'>
            <div className='bg-[var(--main-green)] rounded-full p-4 flex items-center justify-center w-max mb-6'>
              <Picture icon={checkmarkBlackOutlined} height='40' width={40} />
            </div>
            <div className='text-2xl font-medium text-center'>
              Вы уже авторизованы!
            </div>
          </div>
        )}

        {(formStage === 'main' || isBarMenuSuccessOtpStage) && (
          <AuthFormMain
            isBarMenu={isBarMenu}
            compact={isBarMenuSuccess}
            initialPhone={initialPhone}
            isShowTest={isShowTest}
            loginErrorMsg={loginErrorMsg}
            onLoginOrRegisterError={onLoginOrRegisterError}
            setOnLoginOrRegisterError={setOnLoginOrRegisterError}
            userExists={userExists}
            setUserExists={setUserExists}
            getUserCredentials={getUserCredentials}
            authViaSms={authViaSms}
            authViaCall={authViaCall}
            authViaTg={authViaTg}
            onMaxClick={startMaxLogin}
            authViaLoginByPhoneAnyWay={authViaLoginByPhoneAnyWay}
            barMenuRequestCall={barMenuRequestCall}
            setFormStage={setFormStage}
            setIsShowCAPTCHA={setIsShowCAPTCHA}
            cartStore={cartStore}
            authMethods={getAvailableAuthMethods(
              configStore.data?.tinkoff_auth_partner === '1'
            )}
            reinitializeTinkoff={reinitializeTinkoff}
          />
        )}

        {formStage === 'phone-login' &&
          (isMenuAuth ? (
            isBarMenu && !isBarMenuSuccess ? (
              <div className='flex flex-col w-full h-full'>
                <div className='-mx-3'>
                  <Picture
                    icon={barMenuAuthImage}
                    className='w-full h-auto'
                    width='375'
                    height='236'
                  />
                </div>
                <div className='mt-auto'>
                  <AuthFormPhone
                    key={barMenuOtpAction}
                    userPhone={userCredentials.phone}
                    authViaSms={barMenuOtpVerify}
                    isCall={barMenuOtpAction === PeopleSmsAction.Flashcall}
                    onBack={goToMainStage}
                    error={onLoginOrRegisterError}
                    autoSubmit
                    onSmsFallback={
                      barMenuOtpAction === PeopleSmsAction.Flashcall
                        ? () =>
                            barMenuRequestSms({ phone: userCredentials.phone })
                        : undefined
                    }
                  />
                </div>
              </div>
            ) : (
              renderBarMenuSuccessOtp(
                <AuthFormPhone
                  key={barMenuOtpAction}
                  isBarMenuSheet={isBarMenuSuccess}
                  userPhone={userCredentials.phone}
                  authViaSms={barMenuOtpVerify}
                  isCall={barMenuOtpAction === PeopleSmsAction.Flashcall}
                  onBack={goToMainStage}
                  error={onLoginOrRegisterError}
                  autoSubmit
                  onSmsFallback={
                    barMenuOtpAction === PeopleSmsAction.Flashcall
                      ? () =>
                          barMenuRequestSms({ phone: userCredentials.phone })
                      : undefined
                  }
                />
              )
            )
          ) : (
            <AuthFormPhone
              userPhone={userCredentials.phone}
              authViaSms={authViaSms}
              isCall={false}
              onBack={goToMainStage}
            />
          ))}

        {formStage === 'call-login' && (
          <AuthFormPhone
            userPhone={userCredentials.phone}
            authViaSms={authViaCall}
            isCall
            onBack={goToMainStage}
          />
        )}

        {formStage === 'tg-login' &&
          (isBarMenu && !isBarMenuSuccess ? (
            <div className='flex flex-col w-full h-full'>
              <div className='-mx-3'>
                <Picture
                  icon={barMenuAuthImage}
                  className='w-full h-auto'
                  width='375'
                  height='236'
                />
              </div>
              <div className='mt-auto'>
                <AuthFormPhone
                  userPhone={userCredentials.phone}
                  authViaSms={authViaTg}
                  isCall={false}
                  isTelegram
                  onBack={goToMainStage}
                  error={onLoginOrRegisterError}
                />
              </div>
            </div>
          ) : (
            renderBarMenuSuccessOtp(
              <AuthFormPhone
                isBarMenuSheet={isBarMenuSuccess}
                userPhone={userCredentials.phone}
                authViaSms={authViaTg}
                isCall={false}
                isTelegram
                onBack={goToMainStage}
                error={onLoginOrRegisterError}
              />
            )
          ))}

        {formStage === 'max-login' && maxLoginData && (
          <AuthFormMaxLogin
            link={maxLoginData.link}
            ttl={maxLoginData.ttl}
            error={maxLoginError}
            status={maxLoginStatus}
            onBack={exitMaxLogin}
            onRetry={startMaxLogin}
            onConfirm={() => checkMaxLogin(maxLoginData.code, { manual: true })}
          />
        )}

        {formStage === 'email-login' && (
          <AuthFormEmail
            userEmail={userCredentials.email}
            authViaEmail={authViaEmail}
            userExists={userExists}
            goToRestorePassModal={goToRestorePassModal}
          />
        )}

        {formStage === 'restore-pass' && (
          <AuthFormRestorePass
            userEmail={userCredentials.email}
            getUserCredentials={getUserCredentials}
            goToMainStage={goToMainStage}
            resetPass={resetPass}
          />
        )}
      </div>
    );
  }
);

export default AuthForm;
